import { AriaAttributes, ReactNode } from 'react';

import { Button, Chip, chipClasses, Stack, StackProps, styled } from '@mui/material';

import clsx from 'clsx';

import { ellipsize } from '../../styles/mixins';
import Typography, { Props as TypographyProps } from '../Typography';
import { generateClasses } from '../../utils/helper';
import MessageCircleIcon from '../../icons/MessageCircleIcon';

export enum GridAreas {
  ICON = 'icon',
  HEADER = 'header',
  HEADER_META = 'headerMeta',
  DESCRIPTION = 'description',
}

const activityWidgetVariant = ['default', 'primary', 'success', 'error'] as const;

export type ActivityWidgetVariant = (typeof activityWidgetVariant)[number];

export const activityWidgetClasses = generateClasses('ActivityWidget', [
  ...activityWidgetVariant,
  'root',
  GridAreas.ICON,
  GridAreas.HEADER,
  'title',
  'tag',
  GridAreas.HEADER_META,
  GridAreas.DESCRIPTION,
]);

interface WrapperProps extends AriaAttributes {
  className?: string;
  children?: ReactNode;
}

const Wrapper = ({ children, className, ...props }: WrapperProps) => {
  return (
    <div {...props} className={clsx(className, activityWidgetClasses.root)}>
      {children}
    </div>
  );
};

export const Root = styled(Wrapper)(({ theme }) => ({
  [`&.${activityWidgetClasses.root}`]: {
    overflow: 'hidden',
    display: 'grid',
    gridTemplateAreas: `
    "${GridAreas.ICON} ${GridAreas.HEADER} ${GridAreas.HEADER_META}"
    "${GridAreas.ICON} ${GridAreas.DESCRIPTION} ${GridAreas.DESCRIPTION}"
    `,
    gridTemplateColumns: 'auto 1fr auto',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(0, 2.25),
    padding: theme.spacing(2.25),
    backgroundImage: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 6,
    textTransform: 'none',
    textAlign: 'unset',
    width: '100%',
  },
  [`& .${activityWidgetClasses[GridAreas.ICON]}`]: {
    gridArea: GridAreas.ICON,
  },
  [`& .${activityWidgetClasses[GridAreas.HEADER]}`]: {
    gridArea: GridAreas.HEADER,
  },
  [`& .${activityWidgetClasses[GridAreas.HEADER_META]}`]: {
    gridArea: GridAreas.HEADER_META,
  },
  [`& .${activityWidgetClasses[GridAreas.DESCRIPTION]}`]: {
    gridArea: GridAreas.DESCRIPTION,
  },
}));

export const IconWrapper = styled((props: StackProps) => (
  <Stack direction="row" justifyContent="center" alignItems="center" {...props} />
))(({ theme }) => ({
  borderRadius: 3,
  padding: theme.spacing(1.5),
  [`&.${activityWidgetClasses.default}`]: {
    color: theme.palette.grey[600],
    backgroundColor: theme.palette.grey[50],
  },
  [`&.${activityWidgetClasses.primary}`]: {
    color: theme.palette.primary[900],
    backgroundColor: theme.palette.primary[50],
  },
  [`&.${activityWidgetClasses.success}`]: {
    color: theme.palette.success[700],
    backgroundColor: theme.palette.success[50],
  },
  [`&.${activityWidgetClasses.error}`]: {
    color: theme.palette.error[800],
    backgroundColor: theme.palette.error[50],
  },
}));

export const Title = styled((props: TypographyProps) => (
  <Typography variant="textSmBold" component="h6" color="grey.500" {...props} />
))(({ theme }) => ({
  ...ellipsize,
}));

export const Description = styled((props: TypographyProps) => (
  <Typography variant="textXsMedium" color="grey.400" {...props} />
))(({ theme }) => ({
  ...ellipsize,
}));
